import React, { useEffect, useRef, useState } from "react";
import {
  Grid,
  Typography,
  Paper,
  Backdrop,
  CircularProgress,
  Tabs,
  Tab,
  Button,
} from "@mui/material";
import { axiosAPI } from "../../Components/AxiosComponents/Axios";
import { decryptId } from "../../Components/CryptoComponent";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  errorNotify,
  infoNotify,
  successNotify,
} from "../../Components/ToastComponent/ToastNotification";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

import TextInput from "../../Components/InputComponents/TextInput";
import SelectInput from "../../Components/InputComponents/SelectInput";
import CommonButton from "../../Components/InputComponents/CommonButton";
import TextAreaInput from "../../Components/InputComponents/TextAreaInput";

const EditClient = () => {
  const navigate = useNavigate();
  const { id } = useParams(); // this is the encrypted id
  const decryptedId = decryptId(decodeURIComponent(id));
  const location = useLocation();

  const paginationState = location?.state?.pagination || {
    page: 0,
    rowsPerPage: 25,
  };

  const filterData = location?.state?.filterData


  
  const open = location?.state?.open || "";

  const [clientData, setClientData] = useState({
    CD_Name: "",
    CD_Email: "",
    CD_Phone: "",
    CD_Address: "",
    CD_Country: "",
    CD_CompanyType: "",

    CD_ContactPerson: "",
    CD_ContactPerson_Email: "",
    CD_ContactPerson_Phone: "",

    CD_Branches: "",
    CD_UserName: "",
    CD_Status: "",
    CD_Notes: "",
  });

  const [errors, setErrors] = useState({});

  const [initialData, setInitialData] = useState(null);

  const [isDisabledTab, setIsDisabledTab] = useState({
    tab0: true,
    tab1: true,
    tab2: true,
  });

  const tabFields = {
    tab0: [
      "CD_Name",
      "CD_Email",
      "CD_Phone",
      "CD_Address",
      "CD_Country",
      "CD_CompanyType",
    ],
    tab1: [
      "CD_ContactPerson",
      "CD_ContactPerson_Email",
      "CD_ContactPerson_Phone",
    ],
    tab2: ["CD_Branches", "CD_UserName", "CD_Status", "CD_Notes"],
  };

  const compareTabs = (newData) => {
    if (!initialData) return;

    const updatedDisabled = { ...isDisabledTab };

    Object.entries(tabFields).forEach(([tabKey, fields]) => {
      const hasChanged = fields.some((field) => {
        const newVal = newData[field] ?? null;
        const oldVal = initialData[field] ?? null;

        return String(newVal) !== String(oldVal);
      });

      updatedDisabled[tabKey] = !hasChanged;
    });

    setIsDisabledTab(updatedDisabled);
  };

  const inputRefs = {
    CD_Name: useRef(),
    CD_Country: useRef(),
    CD_CompanyType: useRef(),
    CD_ContactPerson_Email: useRef(),
    CD_ContactPerson_Phone: useRef(),
  };

  const [isDisabled, setIsDisabled] = useState(false);
  const [loaderOpen, setLoaderOpen] = useState(false);

  const [loading, setLoading] = useState({});
  const [countryList, setCountryList] = useState([]);
  const [companyTypeList, setCompanyTypeList] = useState([]);

  const mandatoryFields = [
    { field: "CD_Name", label: "Full Name", tab: 0 },
    { field: "CD_Country", label: "Country", tab: 0 },
    { field: "CD_CompanyType", label: "Company Type", tab: 0 },

    {
      field: "CD_ContactPerson_Email",
      label: "Contact Email",
      type: "email",
      tab: 1,
    },
    { field: "CD_ContactPerson_Phone", label: "Contact Phone Number", tab: 1 },

    { field: "CD_Status", label: "Status", tab: 2 },
  ];

  const validateForm = () => {
    let formErrors = {};
    const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;
    const phoneRegex = /^[0-9]{20}$/; // Assuming 20-digit phone numbers for validation

    let firstErrorTab = null;

    for (let { field, label, type, tab } of mandatoryFields) {
      const value = clientData[field];

      // Skip validation if value is exactly 10 asterisks
      if (value === "**********") continue;

      if (!value) {
        formErrors[field] = `${label} is required.`;
        if (firstErrorTab === null) firstErrorTab = tab;
      } else if (type === "email" && !emailRegex.test(value)) {
        formErrors[field] = `Please enter a valid ${label}.`;
        if (firstErrorTab === null) firstErrorTab = tab;
      }
    }

    setErrors(formErrors);

    if (Object.keys(formErrors).length > 0) {
      if (firstErrorTab !== null) {
        setTabValue(firstErrorTab);
      }

      Object.values(formErrors).forEach((error) => {
        errorNotify(error);
      });

      return false;
    }

    return true;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    const updatedData = { ...clientData, [name]: value };

    setClientData(updatedData);

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));

    compareTabs(updatedData);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (validateForm()) {
      setIsDisabled(true);
      setLoaderOpen(true);

      // Filter out fields with value exactly equal to "**********"
      const filteredClientData = Object.entries(clientData).reduce(
        (acc, [key, value]) => {
          if (value !== "**********") {
            acc[key] = value;
          }
          return acc;
        },
        {}
      );

      axiosAPI
        ?.post(`/Client/Edit/${decryptedId}`, filteredClientData)
        .then((res) => {
          if (res?.data?.success) {
            successNotify(res?.data?.message);
            setTimeout(() => {
              navigate(`/Clients`, {
                state: {
                  pagination: paginationState,
                  filterData: filterData,
                  open: open,
                },
              });
            }, 1000);
          } else {
            infoNotify(res?.data?.message);
            setIsDisabled(false);
            setLoaderOpen(false);
          }
        })
        .catch((err) => {
          setIsDisabled(false);
          setLoaderOpen(false);
          errorNotify("Something went Wrong!");
        });
    }
  };

  const getCountries = async () => {
    try {
      setLoading((prev) => ({ ...prev, countries: true }));
      const res = await axiosAPI.get(`/RecruitmentCountry/listCountry`);
      setCountryList(res?.data?.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading((prev) => ({ ...prev, countries: false }));
    }
  };

  const getCompanyType = async () => {
    try {
      setLoading((prev) => ({ ...prev, companyType: true }));
      const res = await axiosAPI.get(`/CompanyType/active/list`);
      setCompanyTypeList(res?.data?.data || []);
    } catch (err) {
      console.error("Active API failed, retrying without 'active'", err);
      try {
        const resFallback = await axiosAPI.get(`/CompanyType/list`);
        setCompanyTypeList(resFallback?.data?.data || []);
      } catch (fallbackErr) {
        console.error("Fallback API also failed", fallbackErr);
      }
    } finally {
      setLoading((prev) => ({ ...prev, companyType: false }));
    }
  };

  const getData = async () => {
    try {
      const res = await axiosAPI.get(`/Client/listbyIdNew/${decryptedId}`);
      if (res?.data?.data) {
        setClientData(res?.data?.data);
        setLoaderOpen(false);
        setInitialData(res?.data?.data);
      }
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    const firstErrorField = mandatoryFields.find(
      (field) => errors[field.field]
    );

    if (firstErrorField && inputRefs[firstErrorField.field]?.current) {
      inputRefs[firstErrorField.field].current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });

      inputRefs[firstErrorField.field].current.focus(); // optional
    }
  }, [errors]);

  useEffect(() => {
    window.scrollTo(0, 0);
    getCountries();
    getCompanyType();
    getData();
  }, []);

  const [tabValue, setTabValue] = useState(0);

  const [tabs, setTabs] = useState([
    "Basic Information",
    "Contact Person Details ",
    "Additional Information",
  ]);

  const handleSubChange = (event, newValue) => {
    setTabValue(newValue);
  };

  return (
    <Paper
      sx={{
        maxWidth: `100vw`,
        padding: 2,
        margin: "auto",
        boxShadow: "none",
        overflowX: "auto",
        fontFamily: "Geist, sans-serif",
        minHeight: "100%",
      }}
    >
      <Typography
        variant="h5"
        sx={{ fontWeight: 600, fontFamily: "Geist, sans-serif" }}
      >
        Edit Client
      </Typography>

      {/* Tabs ========================================================================================================================================================================== */}
      <Grid container spacing={2} sx={{ maxWidth: "100vw", overflow: `auto` }}>
        <Grid item>
          <Tabs
            value={tabValue}
            onChange={handleSubChange}
            indicatorColor="error"
            textColor="error"
            variant="scrollable"
            scrollButtons="auto"
            sx={{ fontFamily: "Geist, sans-serif", marginTop: "10px" }}
          >
            {tabs.map((label, index) => (
              <Tab
                key={label}
                label={label}
                sx={{
                  fontFamily: "Geist, sans-serif",
                  background: tabValue == index ? "#006CB8" : "#f5f9ffff",
                  border: "1px solid lightgrey",
                  borderBottom: "4px solid #EAB308",
                  color: tabValue == index ? "white" : "black",
                  fontWeight: tabValue == index ? 600 : 500,
                  fontSize: `14px`,
                  letterSpacing: "1px",
                  minWidth: "150px",
                  minHeight: "60px",
                  marginRight: "5px",
                }}
              />
            ))}
          </Tabs>
        </Grid>
      </Grid>

      {/* Basic Information */}
      {tabValue == 0 && (
        <Grid
          container
          direction="column"
          justifyContent="space-between"
          sx={{
            border: "1px solid lightgrey",
            padding: "20px",
            minHeight: "250px",
          }}
        >
          {/* Form Fields */}
          <Grid container spacing={2}>
            {/* Client Name */}
            <Grid item xs={12} sm={12} md={4} lg={4} xl={3}>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
              >
                Name <span style={{ color: "red" }}> * </span>
              </Typography>
              <TextInput
                label="Full Name"
                placeholder="Enter Client Name"
                name="CD_Name"
                value={clientData.CD_Name}
                onChange={handleChange}
                inputRef={inputRefs.CD_Name}
                error={!!errors.CD_Name}
                isDisabled={clientData.CD_Name == "**********"}
              />
            </Grid>

            {/* Email */}
            <Grid item xs={12} sm={12} md={4} lg={4} xl={3}>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
              >
                Email
              </Typography>
              <TextInput
                label="Email Address"
                placeholder="Enter Email Address"
                type="email"
                name="CD_Email"
                value={clientData.CD_Email}
                onChange={handleChange}
                isDisabled={clientData.CD_Email == "**********"}
              />
            </Grid>

            {/* Phone Number */}
            <Grid item xs={12} sm={12} md={4} lg={4} xl={3}>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
              >
                Contact Number
              </Typography>
              <TextInput
                label="Phone Number"
                placeholder="Enter Contact Number"
                type="tel"
                name="CD_Phone"
                value={clientData.CD_Phone}
                onChange={handleChange}
                // allowOnlyNumbers={true}
                maxLength={20}
                isDisabled={clientData.CD_Phone == "**********"}
              />
            </Grid>

            {/* Address */}
            <Grid item xs={12} sm={12} md={4} lg={4} xl={3}>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
              >
                Address
              </Typography>
              <TextAreaInput
                label="Address"
                placeholder="Enter Address"
                name="CD_Address"
                value={clientData.CD_Address}
                onChange={handleChange}
                isDisabled={clientData.CD_Address == "**********"}
              />
            </Grid>

            {/* Country */}
            <Grid item xs={12} sm={12} md={4} lg={4} xl={3}>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
              >
                Country <span style={{ color: "red" }}> * </span>
              </Typography>
              <SelectInput
                name="CD_Country"
                value={clientData.CD_Country}
                options={countryList?.map((item) => ({
                  value: item?.CL_ID,
                  label: item?.CL_Name,
                }))}
                placeholder={
                  clientData.CD_Country === "**********"
                    ? "**********"
                    : "Select Country"
                }
                onChange={handleChange}
                inputRef={inputRefs.CD_Country}
                error={!!errors.CD_Country}
                loading={loading?.countries}
                isDisabled={clientData.CD_Country == "**********"}
              />
            </Grid>

            {/* Company Type */}
            <Grid item xs={12} sm={12} md={4} lg={4} xl={3}>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
              >
                Company Type <span style={{ color: "red" }}> * </span>
              </Typography>
              <SelectInput
                name="CD_CompanyType"
                value={clientData.CD_CompanyType}
                options={companyTypeList?.map((item) => ({
                  value: item.CT_Id,
                  label: item.CT_Name,
                }))}
                placeholder={
                  clientData.CD_CompanyType === "**********"
                    ? "**********"
                    : "Select Company Type"
                }
                onChange={handleChange}
                inputRef={inputRefs.CD_CompanyType}
                error={!!errors.CD_CompanyType}
                loading={loading?.companyType}
                isDisabled={clientData.CD_CompanyType == "**********"}
              />
            </Grid>
          </Grid>

          {/* Save Button aligned to bottom-right */}
          <Grid item sx={{ mt: 2 }} display="flex" justifyContent="flex-end">
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={isDisabledTab.tab0}
            >
              {isDisabled ? "Saving..." : "Save"}
            </Button>
          </Grid>
        </Grid>
      )}

      {/* Contact Person Details */}
      {tabValue == 1 && (
        <Grid
          container
          direction="column"
          justifyContent="space-between"
          sx={{
            border: "1px solid lightgrey",
            padding: "20px",
           minHeight: "250px",
          }}
        >
          <Grid container spacing={2}>
            {/* Contact Person Name */}
            <Grid item xs={12} sm={12} md={4} lg={4} xl={3}>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
              >
                Name
              </Typography>
              <TextInput
                label="Contact Person Name"
                placeholder="Enter Contact Person's Name"
                name="CD_ContactPerson"
                value={clientData.CD_ContactPerson}
                onChange={handleChange}
                isDisabled={clientData.CD_ContactPerson == "**********"}
              />
            </Grid>

            {/* Contact Person Email */}
            <Grid item xs={12} sm={12} md={4} lg={4} xl={3}>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
              >
                Email <span style={{ color: "red" }}> * </span>
              </Typography>
              <TextInput
                label="Contact Person Email"
                placeholder="Enter Contact Person's Email Address"
                type="email"
                name="CD_ContactPerson_Email"
                value={clientData.CD_ContactPerson_Email}
                onChange={handleChange}
                inputRef={inputRefs.CD_ContactPerson_Email}
                error={!!errors.CD_ContactPerson_Email}
                isDisabled={clientData.CD_ContactPerson_Email == "**********"}
              />
            </Grid>

            {/* Contact Person Phone Number */}
            <Grid item xs={12} sm={12} md={4} lg={4} xl={3}>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
              >
                Contact Number<span style={{ color: "red" }}> * </span>
              </Typography>
              <TextInput
                label="Phone Number"
                placeholder="Enter Contact Number"
                type="tel"
                name="CD_ContactPerson_Phone"
                value={clientData.CD_ContactPerson_Phone}
                onChange={handleChange}
                // allowOnlyNumbers={true}
                maxLength={20}
                inputRef={inputRefs.CD_ContactPerson_Phone}
                error={!!errors.CD_ContactPerson_Phone}
                isDisabled={clientData.CD_ContactPerson_Phone == "**********"}
              />
            </Grid>
          </Grid>

          {/* Save Button fixed at bottom right */}
          <Grid item sx={{ mt: 2 }} display="flex" justifyContent="flex-end">
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={isDisabledTab.tab1}
            >
              {isDisabled ? "Saving..." : "Save"}
            </Button>
          </Grid>
        </Grid>
      )}

      {tabValue == 2 && (
        <Grid
          container
          direction="column"
          justifyContent="space-between"
          sx={{
            border: "1px solid lightgrey",
            padding: "20px",
            minHeight: "250px",
          }}
        >
          {/* Form Fields Section */}
          <Grid container spacing={2}>
            {/* Branches */}
            <Grid item xs={12} sm={12} md={4} lg={4} xl={3}>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
              >
                Branches
              </Typography>
              <TextInput
                label="Branches"
                placeholder="Branches"
                name="CD_Branches"
                value={clientData.CD_Branches}
                onChange={handleChange}
                isDisabled={clientData.CD_Branches == "**********"}
              />
            </Grid>

            {/* Client Username */}
            <Grid item xs={12} sm={12} md={4} lg={4} xl={3}>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
              >
                Username <span style={{ color: "red" }}> * </span>
              </Typography>
              <TextInput
                label="User Name"
                placeholder="Enter Username"
                name="CI_UserName"
                value={clientData.CD_UserName}
                isDisabled={true}
                error={!!errors.CD_UserName}
                inputRef={inputRefs.CD_UserName}
              />
            </Grid>

            {/* Client Status */}
            <Grid item xs={12} sm={12} md={4} lg={4} xl={3}>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
              >
                Status <span style={{ color: "red" }}> * </span>
              </Typography>
              <SelectInput
                name="CD_Status"
                value={clientData.CD_Status}
                options={[
                  { value: 1, label: "Active" },
                  { value: 2, label: "Inactive" },
                ]}
                placeholder={
                  clientData.CD_Status == "**********"
                    ? "**********"
                    : "Select Status"
                }
                onChange={handleChange}
                error={!!errors.CD_Status}
                isDisabled={clientData.CD_Name == "**********"}
              />
            </Grid>

            {/* Notes */}
            <Grid item xs={12} sm={12} md={4} lg={4} xl={3}>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
              >
                Notes
              </Typography>
              <TextAreaInput
                label="Notes"
                placeholder="Notes"
                name="CD_Notes"
                value={clientData.CD_Notes}
                onChange={handleChange}
                isDisabled={clientData.CD_Notes == "**********"}
              />
            </Grid>
          </Grid>

          {/* Save Button at Bottom Right */}
          <Grid item sx={{ mt: 2 }} display="flex" justifyContent="flex-end">
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={isDisabledTab.tab2}
            >
              {isDisabled ? "Saving..." : "Save"}
            </Button>
          </Grid>
        </Grid>
      )}

      {/* Buttons */}
      <Grid container spacing={2} justifyContent="flex-end" sx={{ mt: 3 }}>
        <Grid item sx={{ padding: "5px" }}>
          <CommonButton
            variant="outlined"
            customColor="#B0BEC5"
            onClick={() => {
              navigate(`/Clients`, {
                state: {
                  pagination: paginationState,
                  filterData: filterData,
                  open: open,
                },
              });
            }}
          >
            Cancel
          </CommonButton>
        </Grid>

        {/* Back - Visible if not on first tab */}
        {tabValue > 0 && (
          <Grid item>
            <CommonButton
              variant="contained"
              startIcon={<ArrowBackIcon />}
              onClick={() => setTabValue((prev) => prev - 1)}
            >
              Back
            </CommonButton>
          </Grid>
        )}

        {/* Next - Visible if not on last tab */}
        {tabValue < tabs?.length - 1 && (
          <Grid item>
            <CommonButton
              variant="contained"
              endIcon={<ArrowForwardIcon />}
              onClick={() => setTabValue((prev) => prev + 1)}
            >
              Next
            </CommonButton>
          </Grid>
        )}

        {/* {tabValue === tabs?.length - 1 && (
          <Grid item sx={{ padding: "5px" }}>
            <CommonButton
              variant="contained"
              customColor="#007BFF"
              onClick={handleSubmit}
              disabled={isDisabled}
            >
              {isDisabled ? "Saving..." : "Save"}
            </CommonButton>
          </Grid>
        )} */}
      </Grid>

      {/* Loader Backdrop */}
      <Backdrop
        open={loaderOpen}
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          color: "#fff",
          backdropFilter: "blur(5px)",
          backgroundColor: "rgba(0, 0, 0, 0.2)",
          flexDirection: "column",
        }}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </Paper>
  );
};

export default EditClient;
